import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Schedule,
  Layers,
  Extension,
  QueueMusic,
  Radio,
  Room,
  PlayArrow,
  Pause,
  Refresh,
  CalendarMonth,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSchedulingDashboard } from '../../hooks/useScheduling';
import { useOrganizations } from '../../hooks/useFleet';

const SchedulingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: organizations } = useOrganizations();
  const defaultOrgId = organizations?.data?.[0]?.id;

  const {
    data: dashboard,
    isLoading,
    error,
    refetch,
  } = useSchedulingDashboard({ organization_id: defaultOrgId });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load scheduling data: {(error as Error)?.message || 'Unknown error'}
      </Alert>
    );
  }

  const stats = [
    {
      title: 'Schedules',
      icon: <CalendarMonth sx={{ color: '#f97316' }} />,
      color: '#f97316',
      onClick: () => navigate('/scheduling/schedules'),
    },
    {
      title: 'Sequences',
      icon: <Layers sx={{ color: '#8b5cf6' }} />,
      color: '#8b5cf6',
      onClick: () => navigate('/scheduling/sequences'),
    },
    {
      title: 'Segments',
      icon: <Extension sx={{ color: '#10b981' }} />,
      color: '#10b981',
      onClick: () => navigate('/scheduling/segments'),
    },
    {
      title: 'Playlists',
      icon: <QueueMusic sx={{ color: '#3b82f6' }} />,
      color: '#3b82f6',
      onClick: () => navigate('/scheduling/playlists'),
    },
    {
      title: 'Channels',
      icon: <Radio sx={{ color: '#ec4899' }} />,
      color: '#ec4899',
      onClick: () => navigate('/scheduling/channels'),
    },
    {
      title: 'Zones',
      icon: <Room sx={{ color: '#06b6d4' }} />,
      color: '#06b6d4',
      onClick: () => navigate('/scheduling/zones'),
    },
  ];

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Scheduling Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={`${dashboard?.day_name || ''} ${formatTime(dashboard?.current_time || '00:00')}`}
            sx={{ bgcolor: '#f9731620', color: '#f97316' }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={4} md={2} key={index}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
              onClick={stat.onClick}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Box sx={{ mb: 1 }}>{stat.icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Now Playing Grid */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Now Playing by Zone
        </Typography>

        {!dashboard?.zones?.length ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Room sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              No zones configured yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create zones in Fleet &gt; Sites to start scheduling content
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {dashboard.zones.map((zone) => (
              <Grid item xs={12} sm={6} md={4} key={zone.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: zone.color || '#333',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate(`/scheduling/zones/${zone.id}`)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: zone.color || '#f97316',
                        }}
                      />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {zone.name}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      {zone.site_name} - {zone.organization_name}
                    </Typography>

                    {zone.now_playing ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PlayArrow sx={{ fontSize: 16, color: '#10b981' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {zone.now_playing.playlist_name ||
                              zone.now_playing.sequence_name ||
                              zone.now_playing.channel_name ||
                              'Unknown'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(zone.now_playing.start_time)} - {formatTime(zone.now_playing.end_time)}
                        </Typography>
                      </Box>
                    ) : zone.active_schedule_name ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Pause sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          No content scheduled now
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          No schedule assigned
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default SchedulingDashboard;
